import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  users: [],
  currentUser: null,
  roles: [],
  origins: [],
  destinations: [],
  suppliers: [],
  loading: {
    users: false,
    currentUser: false,
    roles: false,
    origins: false,
    destinations: false,
    suppliers: false,
    creating: false,
    updating: false,
    deleting: false,
  },
  error: null,
  filters: {
    search: '',
    role: 'all',
    status: 'all', // all, active, inactive
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
};

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    // Loading states
    setUsersLoading: (state, action) => {
      state.loading.users = action.payload;
    },
    setCurrentUserLoading: (state, action) => {
      state.loading.currentUser = action.payload;
    },
    setRolesLoading: (state, action) => {
      state.loading.roles = action.payload;
    },
    setOriginsLoading: (state, action) => {
      state.loading.origins = action.payload;
    },
    setDestinationsLoading: (state, action) => {
      state.loading.destinations = action.payload;
    },
    setSuppliersLoading: (state, action) => {
      state.loading.suppliers = action.payload;
    },
    setCreatingLoading: (state, action) => {
      state.loading.creating = action.payload;
    },
    setUpdatingLoading: (state, action) => {
      state.loading.updating = action.payload;
    },
    setDeletingLoading: (state, action) => {
      state.loading.deleting = action.payload;
    },

    // Data setters
    setUsers: (state, action) => {
      state.users = action.payload;
      state.loading.users = false;
      state.error = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
      state.loading.currentUser = false;
      state.error = null;
    },
    setRoles: (state, action) => {
      state.roles = action.payload;
      state.loading.roles = false;
      state.error = null;
    },
    setOrigins: (state, action) => {
      state.origins = action.payload;
      state.loading.origins = false;
      state.error = null;
    },
    setDestinations: (state, action) => {
      state.destinations = action.payload;
      state.loading.destinations = false;
      state.error = null;
    },
    setSuppliers: (state, action) => {
      state.suppliers = action.payload;
      state.loading.suppliers = false;
      state.error = null;
    },

    // CRUD operations for users
    addUser: (state, action) => {
      state.users.unshift(action.payload);
      state.loading.creating = false;
      state.error = null;
    },
    updateUser: (state, action) => {
      const updatedUser = action.payload;
      const index = state.users.findIndex(u => u.id === updatedUser.id);
      
      if (index !== -1) {
        state.users[index] = updatedUser;
      }
      
      if (state.currentUser?.id === updatedUser.id) {
        state.currentUser = updatedUser;
      }
      
      state.loading.updating = false;
      state.error = null;
    },
    removeUser: (state, action) => {
      const userId = action.payload;
      state.users = state.users.filter(u => u.id !== userId);
      
      if (state.currentUser?.id === userId) {
        state.currentUser = null;
      }
      
      state.loading.deleting = false;
      state.error = null;
    },

    // CRUD operations for roles
    addRole: (state, action) => {
      state.roles.unshift(action.payload);
      state.loading.creating = false;
      state.error = null;
    },
    updateRole: (state, action) => {
      const updatedRole = action.payload;
      const index = state.roles.findIndex(r => r.id === updatedRole.id);
      
      if (index !== -1) {
        state.roles[index] = updatedRole;
      }
      
      state.loading.updating = false;
      state.error = null;
    },
    removeRole: (state, action) => {
      const roleId = action.payload;
      state.roles = state.roles.filter(r => r.id !== roleId);
      state.loading.deleting = false;
      state.error = null;
    },

    // CRUD operations for origins
    addOrigin: (state, action) => {
      state.origins.unshift(action.payload);
      state.loading.creating = false;
      state.error = null;
    },
    updateOrigin: (state, action) => {
      const updatedOrigin = action.payload;
      const index = state.origins.findIndex(o => o.id === updatedOrigin.id);
      
      if (index !== -1) {
        state.origins[index] = updatedOrigin;
      }
      
      state.loading.updating = false;
      state.error = null;
    },
    removeOrigin: (state, action) => {
      const originId = action.payload;
      state.origins = state.origins.filter(o => o.id !== originId);
      state.loading.deleting = false;
      state.error = null;
    },

    // CRUD operations for destinations
    addDestination: (state, action) => {
      state.destinations.unshift(action.payload);
      state.loading.creating = false;
      state.error = null;
    },
    updateDestination: (state, action) => {
      const updatedDestination = action.payload;
      const index = state.destinations.findIndex(d => d.id === updatedDestination.id);
      
      if (index !== -1) {
        state.destinations[index] = updatedDestination;
      }
      
      state.loading.updating = false;
      state.error = null;
    },
    removeDestination: (state, action) => {
      const destinationId = action.payload;
      state.destinations = state.destinations.filter(d => d.id !== destinationId);
      state.loading.deleting = false;
      state.error = null;
    },

    // CRUD operations for suppliers
    addSupplier: (state, action) => {
      state.suppliers.unshift(action.payload);
      state.loading.creating = false;
      state.error = null;
    },
    updateSupplier: (state, action) => {
      const updatedSupplier = action.payload;
      const index = state.suppliers.findIndex(s => s.id === updatedSupplier.id);
      
      if (index !== -1) {
        state.suppliers[index] = updatedSupplier;
      }
      
      state.loading.updating = false;
      state.error = null;
    },
    removeSupplier: (state, action) => {
      const supplierId = action.payload;
      state.suppliers = state.suppliers.filter(s => s.id !== supplierId);
      state.loading.deleting = false;
      state.error = null;
    },

    // Filters and pagination
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination = initialState.pagination;
    },

    // Error handling
    setError: (state, action) => {
      state.error = action.payload;
      Object.keys(state.loading).forEach(key => {
        state.loading[key] = false;
      });
    },
    clearError: (state) => {
      state.error = null;
    },

    // Clear data
    clearUsers: (state) => {
      state.users = [];
      state.currentUser = null;
    },
    clearCrudData: (state) => {
      state.roles = [];
      state.origins = [];
      state.destinations = [];
      state.suppliers = [];
    },
  },
});

export const {
  setUsersLoading,
  setCurrentUserLoading,
  setRolesLoading,
  setOriginsLoading,
  setDestinationsLoading,
  setSuppliersLoading,
  setCreatingLoading,
  setUpdatingLoading,
  setDeletingLoading,
  setUsers,
  setCurrentUser,
  setRoles,
  setOrigins,
  setDestinations,
  setSuppliers,
  addUser,
  updateUser,
  removeUser,
  addRole,
  updateRole,
  removeRole,
  addOrigin,
  updateOrigin,
  removeOrigin,
  addDestination,
  updateDestination,
  removeDestination,
  addSupplier,
  updateSupplier,
  removeSupplier,
  setFilters,
  setPagination,
  resetFilters,
  setError,
  clearError,
  clearUsers,
  clearCrudData,
} = usersSlice.actions;

// Selectors
export const selectUsers = (state) => state.users;
export const selectAllUsers = (state) => state.users.users;
export const selectCurrentUser = (state) => state.users.currentUser;
export const selectRoles = (state) => state.users.roles;
export const selectOrigins = (state) => state.users.origins;
export const selectDestinations = (state) => state.users.destinations;
export const selectSuppliers = (state) => state.users.suppliers;
export const selectUsersLoading = (state) => state.users.loading;
export const selectUsersError = (state) => state.users.error;
export const selectUsersFilters = (state) => state.users.filters;
export const selectUsersPagination = (state) => state.users.pagination;

// Filtered selectors
export const selectFilteredUsers = (state) => {
  const { users } = state.users;
  const { search, role, status } = state.users.filters;
  
  let filteredUsers = users;
  
  // Apply search filter
  if (search) {
    filteredUsers = filteredUsers.filter(user =>
      user.full_name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase())
    );
  }
  
  // Apply role filter
  if (role !== 'all') {
    filteredUsers = filteredUsers.filter(user => user.role?.role_name === role);
  }
  
  // Apply status filter
  if (status !== 'all') {
    const isActive = status === 'active';
    filteredUsers = filteredUsers.filter(user => user.is_active === isActive);
  }
  
  return filteredUsers;
};

export default usersSlice.reducer;